import { Insight } from "react-migration/components/Insight/Insight";
import { LayerTypeLayerSummaryProps } from "../../../types";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { useSelectionSidebarLoading } from "../../../Bundle/useSelectionSidebarLoadingState";
import { KeyIndicatorColor, useKeyIndicatorTags } from "react-migration/lib/map/useKeyIndicatorTags";
import { Tag, TagColor } from "react-migration/components/Tag";
import { ExpandibleList } from "react-migration/components/ExpandibleList/ExpandibleList";
import { useDesignationsByGeometry } from "react-migration/domains/constraints/designation/hooks/useDesignationsByGeometry";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { useWorkbench } from "../../../Bundle/Workbench/WorkbenchContext";
import { SelectionType } from "src/js/stores/map/store";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { convertLengthToAllUnits, getDistanceDisplayUnits } from "react-migration/lib/util/measurements";
import { MeasurementSystem, userStore } from "src/js/stores/user/store";
import { useSnapshot } from "valtio";
import { pointToPolygonDistance } from "@turf/point-to-polygon-distance";

import { useMemo } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

const getAttribute = (distance: SingleDesignation | undefined, key: string) => {
  return distance?.designation_attributes.find((f) => f.key === key)?.value ?? "";
};

const getRoadName = (distance: SingleDesignation | undefined) => {
  return getAttribute(distance, "road_name");
};

const getRoadCount = (distance: SingleDesignation | undefined) => {
  return getAttribute(distance, "all_motor_vehicles");
};

const getRoadType = (distance: SingleDesignation | undefined) => {
  return getAttribute(distance, "road_category");
};

const getPointFromGeometry = (geometry: GeoJSON.Geometry, sourceGeometry: GeoJSON.Polygon) => {
  if (geometry.type === "Point") {
    return geometry;
  }

  // Get closest point
  if (geometry.type === "MultiPoint") {
    const distance = [...geometry.coordinates].sort((a, b) => {
      return (
        pointToPolygonDistance(a, sourceGeometry, {
          units: "meters",
        }) -
        pointToPolygonDistance(b, sourceGeometry, {
          units: "meters",
        })
      );
    });

    return {
      coordinates: distance[0],
      type: "Point",
    } satisfies GeoJSON.Point;
  }

  throw new Error("Unexpected geometry type");
};

export function TrafficLayerSummary({ selection, onLoadingChange, print }: LayerTypeLayerSummaryProps) {
  const { t } = useTranslation();
  const { setDetailSelection } = useWorkbench();
  const { designations, loading } = useDesignationsByGeometry({
    geometry: selection.feature?.geometry ?? null,
    bufferMeters: 1000,
    includeGeometry: true,
    rootCategories: ["traffic_counts_a", "traffic_counts_b", "traffic_counts_m", "traffic_counts_c_u"],
    attributeKeys: ["road_name", "all_motor_vehicles", "road_category", "estimation_method"],
  });

  const orderdRoads = useMemo(() => {
    const formatted = designations.map((designation) => {
      return {
        id: designation.id,
        roadName: getRoadName(designation),
        roadCount: getRoadCount(designation),
        roadType: getRoadType(designation),
        estimationMethod: getAttribute(designation, "estimation_method"),
        geometry: designation.geometry!,
        // Sorry this is kind of ugly
        distance: pointToPolygonDistance(
          getPointFromGeometry(designation.geometry!, selection.feature!.geometry as GeoJSON.Polygon),
          selection.feature!.geometry as GeoJSON.Polygon,
          {
            units: "meters",
          }
        ),
      };
    });

    return formatted.sort((a, b) => a.distance - b.distance);
  }, [designations]);

  const closestRoad = orderdRoads[0];

  useSelectionSidebarLoading(onLoadingChange, loading);

  useKeyIndicatorTags(() => {
    if (closestRoad?.roadCount) {
      return [
        {
          color: KeyIndicatorColor.DEFAULT,
          tag: Number(closestRoad.roadCount).toLocaleString(),
        },
      ];
    }

    return [];
  }, [closestRoad?.roadCount]);

  const { unit } = useSnapshot(userStore.user.settings);

  if (loading) {
    return (
      <div className="relative p-2 h-[168px]">
        <SkeletonLoading cols={2} rows={1} showPadding={false} bgClassName="bg-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2">
        <Insight.Wrapper title="Nearest traffic count">
          <Insight.Body>{closestRoad?.roadName}</Insight.Body>
          <Insight.Footer>{closestRoad?.roadType}</Insight.Footer>
        </Insight.Wrapper>
        <Insight.Wrapper title="Count">
          <Insight.Body>{Number(closestRoad?.roadCount).toLocaleString()}</Insight.Body>
          <Insight.Footer>
            <Tag color={TagColor.DEFAULT} tag={closestRoad?.estimationMethod} />
          </Insight.Footer>
        </Insight.Wrapper>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-xs text-neutral-500">
          Traffic counts within 1km {print && <span>(only showing the nearest 10 results)</span>}
        </span>

        {orderdRoads?.length && (
          <ExpandibleList limit={print ? 10 : 3} showCount printable={print} itemsName="traffic counts">
            {orderdRoads.map((road) => {
              return (
                <div
                  key={road.id}
                  className="relative flex items-center gap-2 p-2 text-xs font-medium cursor-pointer hover:bg-neutral-100"
                >
                  <div className="flex-1 truncate">{road.roadName}</div>

                  <div className="flex justify-end basis-1/6">
                    <div className="text-right shrink-0">{Number(road.roadCount).toLocaleString()}</div>
                  </div>

                  <div className="flex justify-end text-right basis-1/6">
                    <div className="shrink-0">
                      {
                        convertLengthToAllUnits(road.distance ?? 0, "meters")[
                          unit === MeasurementSystem.IMPERIAL ? "miles" : "km"
                        ]
                      }{" "}
                      {getDistanceDisplayUnits(unit, t, "medium")}
                    </div>
                  </div>

                  <button
                    className="absolute inset-0"
                    title={road.roadName}
                    onClick={() => {
                      setDetailSelection({
                        type: SelectionType.TRAFFIC,
                        id: road.id,
                      });
                      logEventV2({
                        name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
                        properties: {
                          cardName: "Traffic counts",
                          action: "goTo",
                          clickDetail: "Traffic count",
                        },
                      });
                    }}
                  />
                </div>
              );
            })}
          </ExpandibleList>
        )}
      </div>
    </div>
  );
}

TrafficLayerSummary.icon = "icon-lt-money-pound-circle-line";
TrafficLayerSummary.title = "Nearest traffic count";
